import { ceramicPackages, type CeramicPackage } from '../data/ceramic-booking'
import { supabase } from './supabase'

export interface CeramicPackageRow {
  id: string
  slug: string
  display_name: string
  product_name: string
  description: string
  durability_label: string
  starting_price_cents: number
  currency: 'AUD'
  badge: string | null
  warranty_eligible: boolean
  warranty_label: string | null
  optional_upgrade: string | null
  featured: boolean
  active: boolean
  sort_order: number
  created_at?: string
  updated_at?: string
}

export interface EditableCeramicPackage {
  id: string
  slug: string
  displayName: string
  productName: string
  description: string
  durabilityLabel: string
  startingPrice: string
  badge: string
  warrantyEligible: boolean
  warrantyLabel: string
  optionalUpgrade: string
  featured: boolean
  active: boolean
  sortOrder: number
}

function rowToPackage(row: CeramicPackageRow): CeramicPackage {
  return {
    id: row.id,
    slug: row.slug,
    displayName: row.display_name,
    productName: row.product_name,
    description: row.description,
    durabilityLabel: row.durability_label,
    startingPriceCents: row.starting_price_cents,
    currency: row.currency,
    badge: row.badge ?? undefined,
    warrantyEligible: row.warranty_eligible,
    warrantyLabel: row.warranty_label ?? undefined,
    optionalUpgrade: row.optional_upgrade ?? undefined,
    featured: row.featured,
    active: row.active,
    cta: `Select ${row.display_name}`,
  }
}

export function mergeCeramicPackages(rows: CeramicPackageRow[] = []): CeramicPackage[] {
  const activeRows = rows
    .filter((row) => row.active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

  return activeRows.length > 0 ? activeRows.map(rowToPackage) : ceramicPackages
}

export function getPackageFromList(packages: CeramicPackage[], packageId?: string) {
  return packages.find((item) => item.id === packageId)
    ?? packages.find((item) => item.featured)
    ?? packages[0]
    ?? ceramicPackages[0]
}

export function mergeEditableCeramicPackages(rows: CeramicPackageRow[] = []): EditableCeramicPackage[] {
  const editableRows = rows.length > 0
    ? [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    : ceramicPackages.map((item, index) => ({
        id: item.id,
        slug: item.slug,
        display_name: item.displayName,
        product_name: item.productName,
        description: item.description,
        durability_label: item.durabilityLabel,
        starting_price_cents: item.startingPriceCents,
        currency: item.currency,
        badge: item.badge ?? null,
        warranty_eligible: item.warrantyEligible,
        warranty_label: item.warrantyLabel ?? null,
        optional_upgrade: item.optionalUpgrade ?? null,
        featured: item.featured ?? false,
        active: item.active,
        sort_order: (index + 1) * 10,
      }))

  return editableRows.map((row, index) => ({
    id: row.id,
    slug: row.slug,
    displayName: row.display_name,
    productName: row.product_name,
    description: row.description,
    durabilityLabel: row.durability_label,
    startingPrice: String(Math.round(row.starting_price_cents / 100)),
    badge: row.badge ?? '',
    warrantyEligible: row.warranty_eligible,
    warrantyLabel: row.warranty_label ?? '',
    optionalUpgrade: row.optional_upgrade ?? '',
    featured: row.featured,
    active: row.active,
    sortOrder: row.sort_order ?? (index + 1) * 10,
  }))
}

export async function fetchCeramicPackageRows(options: { includeInactive?: boolean } = {}) {
  let query = supabase
    .from('ceramic_packages')
    .select('id, slug, display_name, product_name, description, durability_label, starting_price_cents, currency, badge, warranty_eligible, warranty_label, optional_upgrade, featured, active, sort_order, created_at, updated_at')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })

  if (!options.includeInactive) {
    query = query.eq('active', true)
  }

  const { data, error } = await query

  if (error) throw error
  return (data ?? []) as CeramicPackageRow[]
}

export function ceramicPackagePayload(item: EditableCeramicPackage, sortOrder: number) {
  const price = Number(item.startingPrice)

  if (!item.id.trim()) throw new Error('Package ID is required.')
  if (!item.slug.trim()) throw new Error('Package slug is required.')
  if (!item.displayName.trim()) throw new Error('Package display name is required.')
  if (!item.productName.trim()) throw new Error('GYEON product name is required.')
  if (!item.description.trim()) throw new Error('Description is required.')
  if (!item.durabilityLabel.trim()) throw new Error('Durability label is required.')
  if (!Number.isFinite(price) || price < 0) throw new Error('Enter a valid starting price.')

  return {
    id: item.id.trim(),
    slug: item.slug.trim(),
    display_name: item.displayName.trim(),
    product_name: item.productName.trim(),
    description: item.description.trim(),
    durability_label: item.durabilityLabel.trim(),
    starting_price_cents: Math.round(price * 100),
    currency: 'AUD' as const,
    badge: item.badge.trim() || null,
    warranty_eligible: item.warrantyEligible,
    warranty_label: item.warrantyLabel.trim() || null,
    optional_upgrade: item.optionalUpgrade.trim() || null,
    featured: item.featured,
    active: item.active,
    sort_order: sortOrder,
    updated_at: new Date().toISOString(),
  }
}
